/**
 * Controle Store — Snapshot-based transactie reconciliatie
 *
 * Flow: loadSnapshot → snapshot exists? → show data : show "ophalen" button
 *       createSnapshot → server fetches SF + Mollie → saves to Supabase → returns data
 *       saveDagNotitie → persists bank amount, remarks, day status
 *       approveMonth → marks snapshot as approved (read-only)
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface Snapshot {
  id: string;
  month: string;
  status: 'open' | 'goedgekeurd';
  created_by: string | null;
  created_at: string;
  approved_by: string | null;
  approved_at: string | null;
  notes: string | null;
  totaal_sf: number;
  totaal_mollie: number;
  totaal_refunds: number;
  verschil_count: number;
}

export interface ControleTransactie {
  id: string;
  snapshot_id: string;
  bron: 'sf' | 'mollie' | 'mypos' | 'bank';
  extern_id: string;
  bedrag: number;
  methode: 'pin' | 'ideal' | 'bank' | 'contant';
  methode_detail: string | null;
  datum: string; // YYYY-MM-DD
  datum_tijd: string | null;
  werk_order_id: string | null;
  werk_order_naam: string | null;
  factuur_id: string | null;
  opmerkingen_sf: string | null;
  ontvangen_door: string | null;
  mollie_method: string | null;
  mollie_status: string | null;
  mollie_profile_id: string | null;
  is_refund: boolean;
  match_id: string | null;
  match_status: 'matched' | 'unmatched' | 'manual_match';
}

export interface DagNotitie {
  id?: string;
  snapshot_id: string;
  datum: string;
  bank_bedrag: number | null;
  contant_bedrag: number | null;
  opmerking: string | null;
  status: 'open' | 'verklaard';
  updated_by: string | null;
  updated_at: string | null;
}

export interface DagData {
  datum: string;
  pin: { extern: number | null; sf: number; verschil: number };
  ideal: { extern: number | null; sf: number; verschil: number };
  bank: { extern: number | null; sf: number; verschil: number };
  contant: { geregistreerd: number; gecontroleerd: number; envelop: number | null; verschil: number };
  refunds: number;
  notitie: DagNotitie | null;
  sfCount: number;
  mollieCount: number;
  myposCount: number;
  matchedCount: number;
  unmatchedSfCount: number;
  unmatchedMollieCount: number;
}

export interface MaandTotalen {
  pin: { extern: number; sf: number; verschil: number };
  ideal: { extern: number; sf: number; verschil: number };
  bank: { extern: number; sf: number; verschil: number };
  contant: { geregistreerd: number; gecontroleerd: number; envelop: number; verschil: number };
  refunds: number;
  totaalSf: number;
  totaalMollie: number;
  matchedCount: number;
  totalMollieCount: number;
  totalMyposCount: number;
}

interface ControleState {
  selectedMonth: string;
  snapshot: Snapshot | null;
  transacties: ControleTransactie[];
  dagNotities: Map<string, DagNotitie>;
  dagen: DagData[];
  totalen: MaandTotalen;
  verschilCount: number;

  // Mollie profile filter
  profiles: Record<string, string>; // profileId → name
  excludedProfiles: Set<string>;    // profileIds to hide

  // Bank import
  bankImportData: { iban: string; creditsByDay: Map<string, number>; filteredTotal: number; filteredCount: number; entries: { datum: string; bedrag: number; description: string; senderName: string }[] } | null;

  isLoading: boolean;
  isCreating: boolean;
  isSaving: boolean;
  error: string | null;
  expandedDag: string | null;

  setMonth: (month: string) => void;
  loadSnapshot: (month?: string) => Promise<void>;
  createSnapshot: (month?: string) => Promise<void>;
  refreshSnapshot: (month?: string) => Promise<void>;
  approveMonth: () => Promise<void>;
  toggleDagDetail: (datum: string) => void;
  saveDagNotitie: (datum: string, updates: Partial<DagNotitie>) => Promise<void>;
  manualMatch: (id1: string, id2: string) => Promise<void>;
  unmatch: (id: string) => Promise<void>;
  toggleProfile: (profileId: string) => void;
  setBankImportData: (data: { iban: string; creditsByDay: Map<string, number>; filteredTotal: number; filteredCount: number; entries: { datum: string; bedrag: number; description: string; senderName: string }[] } | null) => void;
  importBankBedragen: () => Promise<void>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function currentMonth(): string {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}

function daysInMonth(month: string): string[] {
  const parts = month.split('-').map(Number);
  const count = new Date(parts[0]!, parts[1]!, 0).getDate();
  const days: string[] = [];
  for (let d = 1; d <= count; d++) {
    days.push(`${month}-${String(d).padStart(2, '0')}`);
  }
  return days;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = await getToken();
  if (!token) throw new Error('Not authenticated');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
}

function emptyTotalen(): MaandTotalen {
  return {
    pin: { extern: 0, sf: 0, verschil: 0 },
    ideal: { extern: 0, sf: 0, verschil: 0 },
    bank: { extern: 0, sf: 0, verschil: 0 },
    contant: { geregistreerd: 0, gecontroleerd: 0, envelop: 0, verschil: 0 },
    refunds: 0,
    totaalSf: 0,
    totaalMollie: 0,
    matchedCount: 0,
    totalMollieCount: 0,
    totalMyposCount: 0,
  };
}

// ─── Aggregation ────────────────────────────────────────────────────────────

function aggregate(
  month: string,
  transacties: ControleTransactie[],
  dagNotities: Map<string, DagNotitie>,
  excludedProfiles?: Set<string>,
): { dagen: DagData[]; totalen: MaandTotalen; verschilCount: number } {
  // Filter out excluded Mollie profiles
  const filteredTx = excludedProfiles && excludedProfiles.size > 0
    ? transacties.filter(t => !(t.bron === 'mollie' && t.mollie_profile_id && excludedProfiles.has(t.mollie_profile_id)))
    : transacties;

  const days = daysInMonth(month);
  const totalen = emptyTotalen();
  let verschilCount = 0;

  const dagen: DagData[] = days.map(datum => {
    const dayTx = filteredTx.filter(t => t.datum === datum);
    const notitie = dagNotities.get(datum) || null;

    const sfTx = dayTx.filter(t => t.bron === 'sf');
    const mollieTx = dayTx.filter(t => t.bron === 'mollie' && !t.is_refund);
    const refundTx = dayTx.filter(t => t.bron === 'mollie' && t.is_refund);
    const myposTx = dayTx.filter(t => t.bron === 'mypos' && !t.is_refund);

    const pinSf = round2(sfTx.filter(t => t.methode === 'pin').reduce((s, t) => s + t.bedrag, 0));
    const idealSf = round2(sfTx.filter(t => t.methode === 'ideal').reduce((s, t) => s + t.bedrag, 0));
    const bankSf = round2(sfTx.filter(t => t.methode === 'bank').reduce((s, t) => s + t.bedrag, 0));
    const contantTx = sfTx.filter(t => t.methode === 'contant');
    const contantGeregistreerd = round2(contantTx.reduce((s, t) => s + t.bedrag, 0));
    const contantGecontroleerd = round2(contantTx.filter(t => t.mollie_status === 'gecontroleerd').reduce((s, t) => s + t.bedrag, 0));
    const contantEnvelop = notitie?.contant_bedrag ?? null;
    const contantVerschil = contantEnvelop !== null ? round2(contantGeregistreerd - contantEnvelop) : round2(contantGeregistreerd - contantGecontroleerd);

    const idealExtern = round2(mollieTx.reduce((s, t) => s + t.bedrag, 0));
    const refunds = round2(refundTx.reduce((s, t) => s + t.bedrag, 0));

    const bankExtern = notitie?.bank_bedrag ?? null;
    const pinExtern = myposTx.length > 0 ? round2(myposTx.reduce((s, t) => s + t.bedrag, 0)) : null;

    const pinVerschil = pinExtern !== null ? round2(pinExtern - pinSf) : 0;
    const idealVerschil = mollieTx.length > 0 ? round2(idealExtern - idealSf) : 0;
    const bankVerschil = bankExtern !== null ? round2(bankExtern - bankSf) : 0;

    if (Math.abs(pinVerschil) > 1 || Math.abs(idealVerschil) > 1 || Math.abs(bankVerschil) > 1 || Math.abs(contantVerschil) > 1) {
      verschilCount++;
    }

    // Matching stats
    const matchedMollie = mollieTx.filter(t => t.match_status === 'matched' || t.match_status === 'manual_match');
    const unmatchedSf = sfTx.filter(t => t.methode === 'ideal' && t.match_status === 'unmatched');
    const unmatchedMollie = mollieTx.filter(t => t.match_status === 'unmatched');

    // Accumulate totals
    totalen.pin.sf += pinSf;
    totalen.pin.extern += pinExtern ?? 0;
    totalen.ideal.sf += idealSf;
    totalen.ideal.extern += idealExtern;
    totalen.bank.sf += bankSf;
    totalen.bank.extern += bankExtern ?? 0;
    totalen.contant.geregistreerd += contantGeregistreerd;
    totalen.contant.gecontroleerd += contantGecontroleerd;
    totalen.contant.envelop += contantEnvelop ?? 0;
    totalen.refunds += refunds;
    totalen.matchedCount += matchedMollie.length;
    totalen.totalMollieCount += mollieTx.length;
    totalen.totalMyposCount += myposTx.length;

    return {
      datum,
      pin: { extern: pinExtern, sf: pinSf, verschil: pinVerschil },
      ideal: { extern: mollieTx.length > 0 ? idealExtern : null, sf: idealSf, verschil: idealVerschil },
      bank: { extern: bankExtern, sf: bankSf, verschil: bankVerschil },
      contant: { geregistreerd: contantGeregistreerd, gecontroleerd: contantGecontroleerd, envelop: contantEnvelop, verschil: contantVerschil },
      refunds,
      notitie,
      sfCount: sfTx.length,
      mollieCount: mollieTx.length,
      myposCount: myposTx.length,
      matchedCount: matchedMollie.length,
      unmatchedSfCount: unmatchedSf.length,
      unmatchedMollieCount: unmatchedMollie.length,
    };
  });

  // Finalize totals
  totalen.pin.sf = round2(totalen.pin.sf);
  totalen.pin.verschil = round2(totalen.pin.extern - totalen.pin.sf);
  totalen.ideal.sf = round2(totalen.ideal.sf);
  totalen.ideal.extern = round2(totalen.ideal.extern);
  totalen.ideal.verschil = round2(totalen.ideal.extern - totalen.ideal.sf);
  totalen.bank.sf = round2(totalen.bank.sf);
  totalen.bank.extern = round2(totalen.bank.extern);
  totalen.bank.verschil = round2(totalen.bank.extern - totalen.bank.sf);
  totalen.contant.geregistreerd = round2(totalen.contant.geregistreerd);
  totalen.contant.gecontroleerd = round2(totalen.contant.gecontroleerd);
  totalen.contant.verschil = round2(totalen.contant.geregistreerd - totalen.contant.gecontroleerd);
  totalen.refunds = round2(totalen.refunds);
  totalen.totaalSf = round2(totalen.pin.sf + totalen.ideal.sf + totalen.bank.sf + totalen.contant.geregistreerd);
  totalen.totaalMollie = round2(totalen.ideal.extern);

  return { dagen, totalen, verschilCount };
}

// ─── Store ──────────────────────────────────────────────────────────────────

export const useControleStore = create<ControleState>()((set, get) => ({
  selectedMonth: currentMonth(),
  snapshot: null,
  transacties: [],
  dagNotities: new Map(),
  dagen: [],
  totalen: emptyTotalen(),
  verschilCount: 0,

  profiles: {},
  excludedProfiles: new Set<string>(),
  bankImportData: null,

  isLoading: false,
  isCreating: false,
  isSaving: false,
  error: null,
  expandedDag: null,

  setMonth: (month: string) => {
    set({ selectedMonth: month, expandedDag: null });
    get().loadSnapshot(month);
  },

  loadSnapshot: async (month?: string) => {
    const targetMonth = month || get().selectedMonth;
    set({ isLoading: true, error: null });

    try {
      const response = await apiFetch(`/api/controle?month=${targetMonth}`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();

      const notitieMap = new Map<string, DagNotitie>();
      for (const n of data.dagNotities || []) {
        notitieMap.set(n.datum, n);
      }

      const transacties = (data.transacties || []) as ControleTransactie[];
      const snapshot = data.snapshot as Snapshot | null;

      const profiles = (data.profiles || {}) as Record<string, string>;

      if (snapshot) {
        const excluded = get().excludedProfiles;
        const result = aggregate(targetMonth, transacties, notitieMap, excluded);
        set({
          snapshot,
          transacties,
          dagNotities: notitieMap,
          profiles,
          dagen: result.dagen,
          totalen: result.totalen,
          verschilCount: result.verschilCount,
          isLoading: false,
        });
      } else {
        set({
          snapshot: null,
          transacties: [],
          dagNotities: new Map(),
          profiles: {},
          dagen: [],
          totalen: emptyTotalen(),
          verschilCount: 0,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error('[Controle] loadSnapshot error:', error);
      set({ isLoading: false, error: error instanceof Error ? error.message : 'Fout bij laden' });
    }
  },

  createSnapshot: async (month?: string) => {
    const targetMonth = month || get().selectedMonth;
    set({ isCreating: true, error: null });

    try {
      const response = await apiFetch(`/api/controle?action=create&month=${targetMonth}`, {
        method: 'POST',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `API error: ${response.status}`);
      }

      const data = await response.json();
      const notitieMap = new Map<string, DagNotitie>();
      for (const n of data.dagNotities || []) {
        notitieMap.set(n.datum, n);
      }

      const transacties = (data.transacties || []) as ControleTransactie[];
      const snapshot = data.snapshot as Snapshot;
      const profiles = (data.profiles || {}) as Record<string, string>;
      const excluded = get().excludedProfiles;
      const result = aggregate(targetMonth, transacties, notitieMap, excluded);

      set({
        snapshot,
        transacties,
        dagNotities: notitieMap,
        profiles,
        dagen: result.dagen,
        totalen: result.totalen,
        verschilCount: result.verschilCount,
        isCreating: false,
      });
    } catch (error) {
      console.error('[Controle] createSnapshot error:', error);
      set({ isCreating: false, error: error instanceof Error ? error.message : 'Fout bij ophalen' });
    }
  },

  refreshSnapshot: async (month?: string) => {
    await get().createSnapshot(month);
  },

  approveMonth: async () => {
    const month = get().selectedMonth;
    set({ isSaving: true, error: null });

    try {
      const response = await apiFetch(`/api/controle?action=approve&month=${month}`, {
        method: 'PATCH',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Goedkeuring mislukt');
      }

      const updatedSnapshot = await response.json();
      set({
        snapshot: { ...get().snapshot!, ...updatedSnapshot },
        isSaving: false,
      });
    } catch (error) {
      console.error('[Controle] approveMonth error:', error);
      set({ isSaving: false, error: error instanceof Error ? error.message : 'Fout bij goedkeuren' });
    }
  },

  toggleDagDetail: (datum: string) => {
    set({ expandedDag: get().expandedDag === datum ? null : datum });
  },

  saveDagNotitie: async (datum: string, updates: Partial<DagNotitie>) => {
    const snapshot = get().snapshot;
    if (!snapshot || snapshot.status === 'goedgekeurd') return;

    set({ isSaving: true });

    try {
      const response = await apiFetch('/api/controle?action=dag-notitie', {
        method: 'PATCH',
        body: JSON.stringify({
          snapshot_id: snapshot.id,
          datum,
          ...updates,
        }),
      });

      if (!response.ok) throw new Error('Opslaan mislukt');
      const saved = await response.json() as DagNotitie;

      const notities = new Map(get().dagNotities);
      notities.set(datum, saved);

      const result = aggregate(get().selectedMonth, get().transacties, notities, get().excludedProfiles);
      set({
        dagNotities: notities,
        dagen: result.dagen,
        totalen: result.totalen,
        verschilCount: result.verschilCount,
        isSaving: false,
      });
    } catch (error) {
      console.error('[Controle] saveDagNotitie error:', error);
      set({ isSaving: false });
    }
  },

  manualMatch: async (id1: string, id2: string) => {
    try {
      const response = await apiFetch('/api/controle?action=match', {
        method: 'PATCH',
        body: JSON.stringify({ id1, id2 }),
      });
      if (!response.ok) throw new Error('Match mislukt');

      // Update local state
      const transacties = get().transacties.map(t => {
        if (t.id === id1) return { ...t, match_id: id2, match_status: 'manual_match' as const };
        if (t.id === id2) return { ...t, match_id: id1, match_status: 'manual_match' as const };
        return t;
      });

      const result = aggregate(get().selectedMonth, transacties, get().dagNotities, get().excludedProfiles);
      set({ transacties, dagen: result.dagen, totalen: result.totalen, verschilCount: result.verschilCount });
    } catch (error) {
      console.error('[Controle] manualMatch error:', error);
    }
  },

  unmatch: async (id: string) => {
    try {
      const response = await apiFetch('/api/controle?action=unmatch', {
        method: 'PATCH',
        body: JSON.stringify({ id }),
      });
      if (!response.ok) throw new Error('Unmatch mislukt');

      const tx = get().transacties.find(t => t.id === id);
      const partnerId = tx?.match_id;

      const transacties = get().transacties.map(t => {
        if (t.id === id || t.id === partnerId) {
          return { ...t, match_id: null, match_status: 'unmatched' as const };
        }
        return t;
      });

      const result = aggregate(get().selectedMonth, transacties, get().dagNotities, get().excludedProfiles);
      set({ transacties, dagen: result.dagen, totalen: result.totalen, verschilCount: result.verschilCount });
    } catch (error) {
      console.error('[Controle] unmatch error:', error);
    }
  },

  toggleProfile: (profileId: string) => {
    const excluded = new Set(get().excludedProfiles);
    if (excluded.has(profileId)) {
      excluded.delete(profileId);
    } else {
      excluded.add(profileId);
    }
    const result = aggregate(get().selectedMonth, get().transacties, get().dagNotities, excluded);
    set({ excludedProfiles: excluded, dagen: result.dagen, totalen: result.totalen, verschilCount: result.verschilCount });
  },

  setBankImportData: (data) => {
    set({ bankImportData: data });
  },

  importBankBedragen: async () => {
    const { snapshot, bankImportData } = get();
    if (!snapshot || !bankImportData || snapshot.status === 'goedgekeurd') return;

    set({ isSaving: true, error: null });

    try {
      const dagEntries = Array.from(bankImportData.creditsByDay.entries()).map(([datum, bank_bedrag]) => ({
        datum,
        bank_bedrag,
      }));

      const response = await apiFetch('/api/controle?action=dag-notitie-batch', {
        method: 'PATCH',
        body: JSON.stringify({
          snapshot_id: snapshot.id,
          entries: dagEntries,
          bank_transacties: bankImportData.entries,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Bank import mislukt');
      }

      const result = await response.json();
      const savedNotities = (result.dagNotities || []) as DagNotitie[];
      const savedTransacties = (result.transacties || []) as ControleTransactie[];

      // Update local notities
      const notities = new Map(get().dagNotities);
      for (const n of savedNotities) {
        notities.set(n.datum, n);
      }

      // Update local transacties: remove old bank entries, add new ones
      const transacties = [
        ...get().transacties.filter(t => t.bron !== 'bank'),
        ...savedTransacties,
      ];

      const agg = aggregate(get().selectedMonth, transacties, notities, get().excludedProfiles);
      set({
        transacties,
        dagNotities: notities,
        dagen: agg.dagen,
        totalen: agg.totalen,
        verschilCount: agg.verschilCount,
        bankImportData: null,
        isSaving: false,
      });
    } catch (error) {
      console.error('[Controle] importBankBedragen error:', error);
      set({ isSaving: false, error: error instanceof Error ? error.message : 'Bank import mislukt' });
    }
  },
}));
